/**
 * Phase 8 · Addendum F §33.1 — Sourcing intake (empty state).
 *
 * Layout (Wrangle reference parity):
 *
 *   ┌──────────┬──────────────────────────────────────────┐
 *   │ Sourcing │  [top bar: New search title | Cfg chs]   │
 *   │ ──────── │  ──────────────────────────────────────  │
 *   │ + New    │  [Connector status]                      │
 *   │ Search   │  [Greeting + composer]                   │
 *   │          │  [Suggestion chips → full templates]     │
 *   │ History  │                                          │
 *   │ item 1   │                                          │
 *   │ item 2   │                                          │
 *   └──────────┴──────────────────────────────────────────┘
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PanelLeftOpen, SlidersHorizontal } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { AssistantIntake, INTAKE_GRADIENT_LIGHT, INTAKE_GRADIENT_DARK } from '@modules/agent/components/AssistantIntake';
import type { IntakeSuggestion } from '@modules/agent/intake.config';
import { writePipelineIntake } from '@modules/smart-match/lib/matchCanvasPipeline';
import { AgentMark } from '@shared/components/AgentMark';
import { createSourcingThread } from './lib/sourcingThreads';
import { SourcingHistoryColumn } from './components/SourcingHistoryColumn';
import { SourcingConnectorStatus } from './components/SourcingConnectorStatus';
import { SourcingPreferencesModal } from './components/SourcingPreferencesModal';

/**
 * Suggestion chips — each carries a full candidate search template so clicking
 * populates the composer with a rich, ready-to-submit prompt rather than just
 * the role name.
 */
const SOURCING_SUGGESTIONS: IntakeSuggestion[] = [
  {
    icon: 'sparkles',
    label: 'AI researcher',
    description: 'Applied ML / NLP research, LLMs, Python',
    prompt:
      'Looking for an AI researcher with 3–5 years of applied ML experience.\n\n' +
      'Must-have\n' +
      '- Deep expertise in NLP and large language models (transformers, RLHF, fine-tuning)\n' +
      '- Strong Python, PyTorch or JAX, comfortable with production pipelines\n' +
      '- Experience designing evaluation sets and running offline experiments\n' +
      '- Track record publishing or contributing to open-source ML projects\n\n' +
      'Nice-to-have\n' +
      '- Experience with vector stores (Pinecone, Weaviate, pgvector)\n' +
      '- Familiarity with LangChain / LlamaIndex orchestration\n' +
      '- Prior work in retrieval-augmented generation (RAG)\n\n' +
      'Location: Remote (India / EU timezones). Budget: competitive.',
  },
  {
    icon: 'figma',
    label: 'Design engineer',
    description: 'React + Figma, design systems, 4+ yrs',
    prompt:
      'Seeking a design engineer who bridges product design and frontend engineering.\n\n' +
      'Must-have\n' +
      '- 4+ years of combined design + React/TypeScript experience\n' +
      '- Expert in Figma with the ability to translate designs into pixel-perfect components\n' +
      '- Experience building or contributing to a component library / design system\n' +
      '- Strong grasp of accessibility (WCAG AA) and responsive layout\n\n' +
      'Nice-to-have\n' +
      '- Familiarity with Storybook, Radix UI, or Tailwind CSS\n' +
      '- Prior B2B SaaS experience\n' +
      '- Open-source UI contributions\n\n' +
      'Location: Remote-first, India + US overlap. Budget: competitive.',
  },
  {
    icon: 'layers',
    label: 'Product designer',
    description: 'B2B SaaS, complex workflows, Figma, 5+ yrs',
    prompt:
      'Looking for a senior product designer with 5+ years of experience in B2B SaaS.\n\n' +
      'Must-have\n' +
      '- Strong Figma skills; owns end-to-end design from discovery to handoff\n' +
      '- Experience designing complex multi-step workflows and data-dense interfaces\n' +
      '- Comfortable running user research, usability tests, and synthesis sessions\n' +
      '- Collaborative partner for PMs and engineers in an agile environment\n\n' +
      'Nice-to-have\n' +
      '- Prior AI / ML product surface experience\n' +
      '- Accessibility (WCAG AA) fluency\n' +
      '- HR-tech, recruiting, or enterprise workflow background\n\n' +
      'Location: Remote-first, India + US overlap. Budget: competitive.',
  },
  {
    icon: 'trending-up',
    label: 'Fund manager',
    description: 'Equity / credit funds, AUM >$500M, CFA preferred',
    prompt:
      'Looking for an experienced fund manager to oversee an equity or credit portfolio.\n\n' +
      'Must-have\n' +
      '- 8+ years in asset management or investment management\n' +
      '- Demonstrated track record managing AUM exceeding $500M\n' +
      '- Strong portfolio construction skills and deep capital markets knowledge\n' +
      '- Experience managing LP relationships and quarterly reporting\n\n' +
      'Nice-to-have\n' +
      '- CFA charterholder\n' +
      '- Experience with alternative assets (private credit, PE) in addition to public markets\n' +
      '- Prior fund launch or product structuring experience\n\n' +
      'Location: Onsite / hybrid preferred. Budget: competitive + carry.',
  },
];

const SCOPE_PILL = {
  label: 'Searching across',
  options: ['All channels', 'Internal pool', 'External only'] as string[],
};

function newThreadId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

function firstNameOf(user: { name?: string | null; email?: string | null } | null): string {
  const name = (user?.name ?? '').trim();
  if (name) {
    const first = name.split(/\s+/)[0];
    if (first && first.length > 0) return first;
  }
  const email = (user?.email ?? '').trim();
  if (email) {
    const handle = email.split('@')[0];
    if (handle) return handle.charAt(0).toUpperCase() + handle.slice(1);
  }
  return 'there';
}

export function SourcingIntakePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = useMemo(() => firstNameOf(user), [user]);
  const [fadingOut, setFadingOut] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(true);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = 'Sourcing · GoGrabJob';
    return () => { document.title = prev; };
  }, []);

  const handleSubmit = useCallback(
    (text: string, scope: string | null) => {
      if (fadingOut) return;
      const trimmed = text.trim();
      if (!trimmed) return;
      const id = newThreadId();
      const scopeId =
        scope === 'External only'
          ? 'external'
          : scope === 'Internal pool'
            ? 'internal'
            : 'all-channels';
      const thread = createSourcingThread({
        id,
        prompt: trimmed,
        scopeId,
        createdByUserId: user?.id,
      });
      writePipelineIntake(thread.id, {
        prompt: thread.prompt,
        source: scopeId === 'external' ? 'external' : 'pool',
        createdAt: thread.createdAt,
      });
      setFadingOut(true);
      window.setTimeout(() => navigate(`/sourcing/${thread.id}`), 180);
    },
    [fadingOut, navigate, user?.id],
  );

  return (
    <>
    <div
      className="relative -m-4 flex min-h-full flex-1 overflow-hidden transition-opacity lg:-m-6"
      style={{
        opacity: fadingOut ? 0 : 1,
        transitionDuration: '180ms',
        minHeight: 'calc(100dvh - 56px)',
      }}
      data-ggj-sourcing-intake
    >
      {/* ── Left history rail ─────────────────────────────────────── */}
      {historyOpen && (
        <SourcingHistoryColumn
          activeThreadId={null}
          variant="standalone"
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {/* ── Right content area ──────────────────────────────────── */}
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Pastel gradient backdrop */}
        <div
          aria-hidden
          className="ggj-intake-gradient ggj-intake-gradient--light pointer-events-none absolute inset-0"
          style={{ background: INTAKE_GRADIENT_LIGHT }}
        />
        <div
          aria-hidden
          className="ggj-intake-gradient ggj-intake-gradient--dark pointer-events-none absolute inset-0"
          style={{ background: INTAKE_GRADIENT_DARK }}
        />

        <div className="relative flex min-h-0 flex-1 flex-col">
          {/* ── Top bar: title (center) + Configure channels (right) */}
          <div
            className="flex items-center border-b px-5 py-3"
            style={{ borderColor: 'var(--vscode-border-primary)' }}
          >
            {/* Left: optional history toggle + agent mark + label */}
            <div className="flex items-center gap-2">
              {!historyOpen && (
                <button
                  type="button"
                  onClick={() => setHistoryOpen(true)}
                  aria-label="Show history panel"
                  title="Show history"
                  className="flex h-7 w-7 items-center justify-center rounded-[8px] transition-colors hover:bg-[var(--vscode-bg-hover)]"
                  style={{ color: 'var(--vscode-text-tertiary)' }}
                >
                  <PanelLeftOpen className="h-4 w-4" aria-hidden />
                </button>
              )}
              <AgentMark className="h-4 w-4" ariaLabel={null} />
              <span
                className="text-[12px] font-semibold uppercase tracking-wide"
                style={{ color: 'var(--vscode-text-tertiary)' }}
              >
                New search
              </span>
            </div>

            {/* Center: spacer */}
            <div className="flex-1" />

            {/* Right: Preferences */}
            <button
              type="button"
              onClick={() => setPrefsOpen(true)}
              className="inline-flex h-8 items-center gap-1.5 rounded-[10px] border px-2.5 text-[12px] font-medium transition-colors hover:bg-[var(--vscode-bg-hover)]"
              style={{
                borderColor: 'var(--vscode-border-primary)',
                color: 'var(--vscode-text-primary)',
                background: 'var(--vscode-bg-elevated)',
                boxShadow: '0 1px 2px rgba(17, 24, 39, 0.04)',
              }}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
              Preferences
            </button>
          </div>

          {/* ── Main area: connector status + composer ───────────── */}
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-4 py-4">
            <div className="flex w-full max-w-[680px] flex-col gap-3">
              <SourcingConnectorStatus variant="full" />

              <AssistantIntake
                greeting={'Hey {firstName}, which candidates should we find today?'}
                placeholder="Describe the candidate you need. e.g. Senior React engineer in Bangalore with SaaS experience…"
                scopePill={SCOPE_PILL}
                suggestions={SOURCING_SUGGESTIONS}
                beta={false}
                entityPlural="candidates"
                firstName={firstName}
                onSubmit={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* Preferences modal */}
    {prefsOpen && <SourcingPreferencesModal onClose={() => setPrefsOpen(false)} />}
    </>
  );
}
